
import Vue from "vue";
import {
  createOfficePromotion,
  deactivateOfficePromotion,
  getCurrentAdminRole,
  getOfficePromotions,
  OfficePromotionAdminRow,
  OfficePromotionAdminInput,
  reactivateOfficePromotion,
  updateOfficePromotion
} from "../../api";

const emptyForm = (): OfficePromotionAdminInput => ({
  state_code: "BC",
  promotion_start_date: "",
  promotion_cutoff_date: "",
  promotion_duration_days: 90,
  is_active: true
});

export default Vue.extend({
  data() {
    return {
      loading: false,
      saving: false,
      error: "",
      success: "",
      promotions: [] as OfficePromotionAdminRow[],
      dialog: false,
      turnOffDialog: false,
      formValid: false,
      editingId: null as number | null,
      pendingTurnOff: null as OfficePromotionAdminRow | null,
      form: emptyForm(),
      regions: [
        "AB",
        "BC",
        "MB",
        "NB",
        "NL",
        "NS",
        "NT",
        "NU",
        "ON",
        "PE",
        "QC",
        "SK",
        "YT",
        "AK",
        "AL",
        "AR",
        "AZ",
        "CA",
        "CO",
        "CT",
        "DE",
        "FL",
        "GA",
        "HI",
        "IA",
        "ID",
        "IL",
        "IN",
        "KS",
        "KY",
        "LA",
        "MA",
        "MD",
        "ME",
        "MI",
        "MN",
        "MO",
        "MS",
        "MT",
        "NC",
        "ND",
        "NE",
        "NH",
        "NJ",
        "NM",
        "NV",
        "NY",
        "OH",
        "OK",
        "OR",
        "PA",
        "RI",
        "SC",
        "SD",
        "TN",
        "TX",
        "UT",
        "VA",
        "VT",
        "WA",
        "WI",
        "WV",
        "WY"
      ],
      headers: [
        { text: "Province/state", value: "state_code" },
        { text: "Signup window", value: "signupWindow", sortable: false },
        { text: "Duration", value: "duration", sortable: false },
        { text: "On/Off", value: "onOff" },
        { text: "Status", value: "status" },
        { text: "Timestamps", value: "timestamps", sortable: false },
        { text: "Actions", value: "actions", sortable: false, align: "end" }
      ],
      rules: {
        required: (value: string | number) =>
          (value !== null && value !== undefined && value !== "") || "Required",
        positiveInt: (value: number) =>
          (Number.isInteger(Number(value)) && Number(value) >= 1) ||
          "Must be a positive whole number"
      }
    };
  },
  async created() {
    try {
      const admin = await getCurrentAdminRole();
      if (!admin.isDeveloper) {
        this.$router.replace({ name: "home" });
        return;
      }
      await this.load();
    } catch (error) {
      this.error = this.errorMessage(error);
    }
  },
  methods: {
    errorMessage(error: unknown): string {
      return error instanceof Error
        ? error.message
        : "Unable to complete the request";
    },
    formatTs(value: string): string {
      if (!value) return "—";
      try {
        return new Date(value).toISOString().replace("T", " ").slice(0, 19) + "Z";
      } catch (e) {
        return value;
      }
    },
    dateOrderRule(value: string): boolean | string {
      return (
        !value ||
        !this.form.promotion_start_date ||
        value >= this.form.promotion_start_date ||
        "Cutoff must be on or after signup start"
      );
    },
    async load() {
      this.loading = true;
      try {
        this.promotions = await getOfficePromotions();
      } catch (error) {
        this.error = this.errorMessage(error);
      } finally {
        this.loading = false;
      }
    },
    statusColor(status: string): string {
      if (status === "Active") return "green";
      if (status === "Scheduled") return "blue";
      if (status === "Expired") return "orange";
      return "grey";
    },
    openCreate() {
      this.editingId = null;
      this.form = emptyForm();
      this.dialog = true;
      this.$nextTick(() => (this.$refs.form as any)?.resetValidation());
    },
    openEdit(item: OfficePromotionAdminRow) {
      this.editingId = item.id;
      this.form = {
        state_code: item.state_code,
        promotion_start_date: item.promotion_start_date,
        promotion_cutoff_date: item.promotion_cutoff_date,
        promotion_duration_days: item.promotion_duration_days,
        is_active: item.is_active
      };
      this.dialog = true;
      this.$nextTick(() => (this.$refs.form as any)?.resetValidation());
    },
    closeDialog() {
      this.dialog = false;
    },
    async save() {
      if (!(this.$refs.form as any)?.validate()) return;
      this.saving = true;
      this.error = "";
      try {
        if (this.editingId) {
          await updateOfficePromotion(this.editingId, {
            promotion_start_date: this.form.promotion_start_date,
            promotion_cutoff_date: this.form.promotion_cutoff_date,
            promotion_duration_days: this.form.promotion_duration_days
          });
          this.success = "Promotion updated. Granted location end dates unchanged.";
        } else {
          await createOfficePromotion(this.form);
          this.success = "Office Promotion created.";
        }
        this.dialog = false;
        await this.load();
      } catch (error) {
        this.error = this.errorMessage(error);
      } finally {
        this.saving = false;
      }
    },
    confirmTurnOff(item: OfficePromotionAdminRow) {
      this.pendingTurnOff = item;
      this.turnOffDialog = true;
    },
    async turnOff() {
      if (!this.pendingTurnOff) return;
      this.saving = true;
      this.error = "";
      try {
        await deactivateOfficePromotion(this.pendingTurnOff.id);
        this.success =
          "Promotion turned off. New locations will not qualify; mid-promo offices keep their end dates.";
        this.turnOffDialog = false;
        this.pendingTurnOff = null;
        await this.load();
      } catch (error) {
        this.error = this.errorMessage(error);
      } finally {
        this.saving = false;
      }
    },
    async turnOn(item: OfficePromotionAdminRow) {
      this.saving = true;
      this.error = "";
      try {
        await reactivateOfficePromotion(item.id);
        this.success = "Promotion turned on.";
        await this.load();
      } catch (error) {
        this.error = this.errorMessage(error);
      } finally {
        this.saving = false;
      }
    }
  }
});
